<script setup lang="ts">
import { ButtonVariant, OrButtonV3 as OrButton } from '@onereach/ui-components';
import { AccountData } from '@or-sdk/accounts';
import { startAuthentication } from '@simplewebauthn/browser';
import axios from 'axios';

import useI18n from '@/composables/useI18n';
import { useAuthStore } from '@/stores/auth/auth';

import config from '../../config';

import { getFormatedError } from './utils/formatAuthError';
const { translate } = useI18n();


const props = withDefaults(defineProps<{
  label?: string;
  buttonType?: `${ButtonVariant}`;
  keepLoggedIn: boolean;
  accountData: AccountData;
  disabled?: boolean;
}>(), {
  label: '',
  buttonType: ButtonVariant.Outlined,
  keepLoggedIn: false,
  disabled: false,
});

const emit = defineEmits(['error']);

const passkeyLoading = defineModel<boolean>('passkeyLoading', { default: false });

const authStore = useAuthStore();

async function handlePasskeyLogin() {
  passkeyLoading.value = true;
  try {
    // Get passkey authentication options from SDK API
    const authenticationOptionsResponse = await axios.get(
      `${config.SDK_API_URL}/multi-user/passkey/authentication`,
    );

    const authenticationOptions = authenticationOptionsResponse.data;

    // Start passkey authentication using SimpleWebAuthn
    const assertionResponse = await startAuthentication(authenticationOptions);
    // Transform the response to match API expected format
    const payload = {
      credential: {
        id: assertionResponse.id,
        rawId: assertionResponse.rawId,
        response: assertionResponse.response,
        type: assertionResponse.type,
      },
      challenge: authenticationOptions.challenge,
      keepLoggedIn: props.keepLoggedIn,
    };

    await axios.post(
      `${config.SDK_API_URL}/multi-user/passkey/verification`,
      payload,
      { withCredentials: true },
    );
    await authStore.authorizeMult({
      keepLoggedIn: props.keepLoggedIn,
      accountData: props.accountData,
    });
  } catch (e: any) {
    const { message } = getFormatedError(e.response?.data?.message || e, translate);
    emit('error', message);
  } finally {
    passkeyLoading.value = false;
  }
}
</script>

<template>
  <div class="w-full">
    <OrButton
      color="primary"
      class="w-full"
      :variant="buttonType"
      :loading="passkeyLoading"
      :disabled="disabled"
      @click="handlePasskeyLogin"
    >
      <svg
        v-if="true"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17 8H18C19.1046 8 20 8.89543 20 10V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V10C4 8.89543 4.89543 8 6 8H7M17 8V6C17 3.79086 15.2091 2 13 2H11C8.79086 2 7 3.79086 7 6V8M17 8H7M12 13V15M9 13C9 13.5523 9.44772 14 10 14H14C14.5523 14 15 13.5523 15 13C15 12.4477 14.5523 12 14 12H10C9.44772 12 9 12.4477 9 13Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      &nbsp;
      {{ label || $t('login.login.Sign in with Passkey') }}
    </OrButton>
  </div>
</template>
